import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useEffect, useContext } from 'react'
import { throttle } from 'lodash'
import {
  CMasthead,
  CMastheadFragments,
  CMastheadBlock,
  CSeomatic,
  SeomaticData,
  CImageCopyBlocksFragments,
  CImageCopyBlocksBlock,
  CImageCopyBlocks,
  CFindADoctor,
  CCenteredCopy,
  CBackgroundImageCopy,
  CBackgroundImageCopyFragments,
  CCtaBoxFragments,
  CBackgroundImageCopyBlock,
  CCtaBoxBlock,
  CCounter,
  CCtaBox,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { HeaderContext } from 'ui/context/header'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')
  
  if (siteInfo.code === 'en-IN') {
    return {
      notFound: true,
    }
  }

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_nightvision_Entry {
            masthead {
              ...masthead
            }
            imageCopyBlocks {
              ...imageCopyBlocks
            }
            backgroundImageCopy {
              ...backgroundImageCopy
            }
            imageCopyBlocks2 {
              ...imageCopyBlocks2
            }
            ctaBox {
              ...ctaBox
            }
          }
        }
      }
      ${CMastheadFragments.field}
      ${CImageCopyBlocksFragments.field}
      ${CBackgroundImageCopyFragments.field}
      ${CImageCopyBlocksFragments.field2}
      ${CCtaBoxFragments.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      transparentHeader: true,
      headerTheme: 'dark',
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    imageCopyBlocks: Array<CImageCopyBlocksBlock>
    backgroundImageCopy: Array<CBackgroundImageCopyBlock>
    imageCopyBlocks2: Array<CImageCopyBlocksBlock>
    ctaBox: Array<CCtaBoxBlock>
  }
  global: CraftGlobals
  transparentHeader: boolean
  headerTheme: string
}

export default function Nightvision({ entry, global }: Props) {
  const headerContext = useContext(HeaderContext)

  const handleWindowScroll = () => {
    const computeIsClear: boolean = window.pageYOffset === 0
    headerContext?.setIsClear(computeIsClear)
  }

  const handleWindowScrollThrottled = throttle(handleWindowScroll, 100)

  useEffect(() => {
    window.addEventListener('scroll', handleWindowScrollThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
    }
  }, [])

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CMasthead field={entry.masthead} />
      <div className="overflow-hidden bg-neutral-black">
        <CImageCopyBlocks field={entry.imageCopyBlocks} />
        <CBackgroundImageCopy field={entry.backgroundImageCopy} />
        <CImageCopyBlocks field={entry.imageCopyBlocks2} />
        <CCounter field={global.counter} />
      </div>
      <CFindADoctor field={global.findADoctor} />
      <CCtaBox field={entry.ctaBox} />
      <CCenteredCopy field={global.centeredCopy} />
    </DefaultLayout>
  )
}
